import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Checkbox, Chip, CircularProgress, Modal, Snackbar, Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FiSearch } from 'react-icons/fi';
import DeleteIcon from '@mui/icons-material/Delete';
import SliderCreate from './SliderCreate';
import SliderEdit from './SliderEdit';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';

const truncateText = (text, maxLength = 70) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
};

const SliderList = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [openEdit, setOpenEdit] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [selectedSlider, setSelectedSlider] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [confirmDelete, setConfirmDelete] = useState(false);

    const handleOpen = (slider) => {
        setSelectedSlider(slider);
        setOpenEdit(true);
    };

    const handleClose = () => {
        setOpenEdit(false);
        setSelectedSlider(null);
    };

    const handleOpenCreate = () => {
        setOpenCreate(true);
    };

    const handleCloseCreate = () => {
        setOpenCreate(false);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleConfirmDeleteOpen = () => {
        if (selectedRows.length > 0) {
            setConfirmDelete(true);
        } else {
            setSnackbarMessage('Please select at least one slider to delete');
            setSnackbarSeverity('warning');
            setOpenSnackbar(true);
        }
    };

    const handleConfirmDeleteClose = () => {
        setConfirmDelete(false);
    };

    const handleBulkDelete = async () => {
        try {
            const deletePromises = selectedRows.map(id =>
                fetch(`${BASE_URL}/sliders/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }).then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to delete slider with ID ${id}`);
                    }
                    return response;
                })
            );
            const responses = await Promise.all(deletePromises);
            const allSuccessful = responses.every(response => response.ok);

            if (allSuccessful) {
                setSliders(sliders.filter(slider => !selectedRows.includes(slider.id)));
                setSelectedRows([]);
                setSnackbarMessage(`Successfully deleted ${selectedRows.length} slider(s)`);
                setSnackbarSeverity('success');
            } else {
                setSnackbarMessage('Failed to delete some sliders');
                setSnackbarSeverity('error');
            }
        } catch (error) {
            console.error('Error deleting sliders:', error);
            setSnackbarMessage('Error deleting sliders: ' + error.message);
            setSnackbarSeverity('error');
        } finally {
            setOpenSnackbar(true);
            setConfirmDelete(false);
        }
    };

    const fetchSliders = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/sliders?page=${page + 1}&limit=${rowsPerPage}`);
            if (!response.ok) {
                throw new Error('Failed to fetch sliders');
            }
            const data = await response.json();

            const formattedSliders = (data.sliders || []).map(slider => ({
                id: slider._id || `fallback-${Math.random()}`,
                clientName: slider.client_name || 'Unknown Client',
                trending: slider.Trending ? 'Yes' : 'No',
                image: slider.slider_img || null,
                link: slider.slider_link || '#',
                status: slider.status || 'Pending',
            }));

            setSliders(formattedSliders);
            setTotalRows(data.total || formattedSliders.length);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSliders();
    }, [page, rowsPerPage]);

    const handleSliderUpdated = () => {
        fetchSliders();
    };

    const handleSliderCreated = () => {
        fetchSliders();
        handleCloseCreate();
    };

    const columns = [
        {
            field: 'select',
            headerName: '',
            width: 70,
            renderCell: (params) => (
                <Checkbox
                    checked={selectedRows.includes(params.row.id)}
                    onChange={(event) => {
                        setSelectedRows(
                            event.target.checked
                                ? [...selectedRows, params.row.id]
                                : selectedRows.filter(id => id !== params.row.id)
                        );
                    }}
                    sx={{
                        color: '#2e7d32',
                        '&.Mui-checked': {
                            color: '#2e7d32',
                        },
                    }}
                />
            ),
        },
        {
            field: 'name',
            headerName: <strong style={{ color: "#24803F" }}>Brand Name</strong>,
            flex: 1,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 1,
                        p: 1,
                        bgcolor: '#f5f5f5',
                        height: '100px',
                        width: '100%',
                        gap: 2,
                    }}>
                        <motion.img
                            src={params.row.image || `https://via.placeholder.com/100?text=${params.row.clientName?.[0] || 'N'}`}
                            style={{ width: 100, height: 50, borderRadius: 1, objectFit: 'cover' }}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.4 }}
                        />
                        <Box
                            sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}
                        >
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, cursor: 'pointer', color: '#2e7d32', fontSize: '16px' }}
                                    onClick={() => handleOpen(params.row)}
                                >
                                    {truncateText(params.row.clientName)}
                                </Typography>
                            </motion.div>
                        </Box>
                    </Box>
                </motion.div>
            ),
        },
        {
            field: 'trending',
            headerName: <strong style={{ color: "#24803F" }}>Trending</strong>,
            width: 150,
            flex: 1,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                >
                    <strong>{params.value}</strong>
                </motion.div>
            ),
        },
        {
            field: 'link',
            headerName: <strong style={{ color: "#24803F" }}>Visit Link</strong>,
            width: 150,
            flex: 1,
            renderCell: (params) => (
                <motion.a
                    href={params.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    whileHover={{ color: '#2e7d32', scale: 1.05 }}
                >
                    Visit Link
                </motion.a>
            ),
        },
        {
            field: 'status',
            headerName: <strong style={{ color: "#24803F" }}>Status</strong>,
            width: 120,
            renderCell: (params) => {
                let chipColor = 'default';
                let chipSx = {};
                if (params.value === 'live') {
                    chipColor = 'success';
                    chipSx = { bgcolor: '#2e7d32', color: '#fff' };
                } else if (params.value === 'pause') {
                    chipColor = 'warning';
                    chipSx = { bgcolor: '#ffe066', color: '#333' };
                } else if (params.value === 'Rejected') {
                    chipColor = 'default';
                    chipSx = { bgcolor: '#bdbdbd', color: '#fff' };
                }
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.3 }}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: "center",
                            gap: 1,
                            height: "10vh",
                        }}>
                            {params.row.trending === 'Yes' && (
                                <motion.div
                                    initial={{ opacity: 1 }}
                                    animate={{ opacity: [1, 0.3, 1] }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: '#2e7d32',
                                        boxShadow: '0 0 8px 2px #00ff00',
                                    }}
                                />
                            )}
                            {params.row.trending === 'No' && params.value === 'pause' && (
                                <motion.div
                                    initial={{ opacity: 1 }}
                                    animate={{ opacity: [1, 0.3, 1] }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                    style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: '#ffe066',
                                        boxShadow: '0 0 8px 2px #ffe066',
                                    }}
                                />
                            )}
                            <Chip
                                label={params.value}
                                color={chipColor}
                                size="small"
                                sx={chipSx}
                            />
                        </Box>
                    </motion.div>
                );
            },
        },
    ];

    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress />
                </Box>
            </motion.div>
        );
    }

    if (error) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ mt: 8 }}>
                    <Typography color="error">Error: {error}</Typography>
                </Box>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Box sx={{ mt: 2, borderRadius: 2, p: 3, bgcolor: '#fff', boxShadow: 1 }}>
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        Slider
                    </Typography>
                    <Typography variant="body2" color="#2e7d32" sx={{ mb: 2 }}>
                        Dashboard • Slider • List
                    </Typography>
                </motion.div>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <TextField
                            placeholder="Search..."
                            size="small"
                            InputProps={{
                                startAdornment: <FiSearch style={{ marginRight: 8, color: '#9ca3af' }} />,
                            }}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                sx={{
                                    borderColor: '#9ca3af',
                                    color: '#9ca3af',
                                    '&:hover': { borderColor: 'tomato', color: 'red' },
                                }}
                                startIcon={<DeleteIcon />}
                                onClick={handleConfirmDeleteOpen}
                                disabled={selectedRows.length === 0}
                            >
                                Delete Selected ({selectedRows.length})
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    backgroundColor: '#2e7d32',
                                    '&:hover': { backgroundColor: '#45a049' },
                                    fontWeight: "bold",
                                }}
                                onClick={handleOpenCreate}
                            >
                                Add Slider
                            </Button>
                        </Box>
                    </motion.div>
                </Box>
                <DataGrid
                    rows={sliders}
                    columns={columns}
                    pageSize={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    autoHeight
                    rowHeight={100}
                    getRowId={(row) => row.id}
                    pagination
                    rowCount={totalRows}
                    paginationMode="server"
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newPageSize) => setRowsPerPage(newPageSize)}
                    sx={{
                        borderRadius: 2,
                        '& .MuiDataGrid-columnHeaders': { bgcolor: '#f9fafb' },
                    }}
                />

                <SliderEdit
                    open={openEdit}
                    handleClose={handleClose}
                    slider={selectedSlider}
                    onSliderUpdated={handleSliderUpdated}
                />

                <Modal
                    open={openCreate}
                    onClose={handleCloseCreate}
                    aria-labelledby="create-slider-modal-title"
                    aria-describedby="create-slider-modal-description"
                >
                    {/* <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                    > */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 650,
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Typography
                            id="create-slider-modal-title"
                            variant="h6"
                            component="h2"
                            sx={{ color: '#2e7d32', mb: 2 }}
                        >
                            Add Slider
                        </Typography>
                        <SliderCreate
                            onSliderCreated={handleSliderCreated}
                            handleClose={handleCloseCreate}
                        />
                    </Box>
                    {/* </motion.div> */}
                </Modal>

                <Modal
                    open={confirmDelete}
                    onClose={handleConfirmDeleteClose}
                    aria-labelledby="confirm-delete-modal-title"
                    aria-describedby="confirm-delete-modal-description"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 400,
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Typography id="confirm-delete-modal-title" variant="h6" component="h2" sx={{ color: 'red' }}>
                                Confirm Deletion
                            </Typography>
                            <Typography id="confirm-delete-modal-description" variant="body1">
                                Are you sure you want to delete {selectedRows.length} selected slider(s)?
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleConfirmDeleteClose}
                                    sx={{ color: '#9ca3af', borderColor: '#9ca3af' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleBulkDelete}
                                    sx={{ backgroundColor: 'red', '&:hover': { backgroundColor: 'darkred' } }}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </Box>
                    </motion.div>
                </Modal>

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </motion.div>
    );
};

export default SliderList;